// Aba de Oportunidades (tabela + filtros + ações sync/triagem/arquivar/análise/proposta).
// Extraído de ComercialLicitacoes.tsx em 2026-05-23 (frontend wave 3B).
// 2026-09-02 (passo 3 do plano de licitações) — a coluna de data virou
// "Prazo" e passou a mostrar o PRAZO PARA PROPOSTA que o backend calcula
// (`prazo_efetivo`), não a abertura da janela; a régua (dias/cor/rótulo) e a
// tradução da situação saíram daqui para `prazo.ts`, que o modal também usa.
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card, Button, Input, Select, Tag, Table, Typography, Space,
  Checkbox, App as AntApp, Flex, Empty, InputNumber, Tooltip, Badge, Alert, Popconfirm,
  Modal, Statistic, Spin, Descriptions,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, RobotOutlined, ClockCircleOutlined, DollarOutlined, BellFilled,
  StopOutlined, InboxOutlined, HistoryOutlined, UndoOutlined, RadarChartOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr } from '../../utils/dataFmt';
import { Se } from '../../components/Se';
import { usePerms } from '../../hooks/usePerms';
import { Licitacao } from './types';
import { STATUS_INTERNO_CFG } from './constants';
import { classificarPrazoEdital, situacaoLegivel, prazoEfetivoCliente, fmtDataHoraCurta } from './prazo';
import { ehAberta, propostaJaFoi } from './pipeline/etapas';
import { radarApi, DIAS_ANTECEDENCIA_PADRAO, textoDoPipeline } from '../meu-radar/api';
import { ModalLicitacao } from './ModalLicitacao';
import { ModalDecisao } from './ModalDecisao';
import { fitTecnicoUI } from './fitTecnico';
import { tituloDoObjeto } from './tituloDoObjeto';

const { Text } = Typography;

// Valor da linha: o mais atualizado entre as fontes quando a licitação veio
// pelas duas (07/10/2026); `numeric` do Postgres chega como texto.
const valorDaLinha = (l: Licitacao): number | null => {
  const v = l.valor_unificado ?? l.valor_estimado;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function AbaOportunidades() {
  const { message, modal } = AntApp.useApp();
  const [licitacoes, setLicitacoes] = useState<Licitacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [sincronizandoConlic, setSincronizandoConlic] = useState(false);
  const [triando, setTriando] = useState(false);
  const [statusTri, setStatusTri] = useState<{ pendentes: number; triadas: number; total: number } | null>(null);
  const [filtros, setFiltros] = useState({
    status_interno: '', uf: '', modalidade: '', busca: '',
    fit_score_min: '', incluir_arquivadas: '', incluir_vencidas: '',
    apenas_com_alteracoes: '', // L14b
  });
  // L14b (2026-06-03) — count global de alteracoes pendentes
  const [pendentesTotal, setPendentesTotal] = useState(0);
  const [aberta, setAberta] = useState<Licitacao | null>(null);
  // ModalDecisao: licitação + análises carregadas via /detalhe
  const [decisaoOpen, setDecisaoOpen] = useState<{ lic: Licitacao; analises: any[] } | null>(null);
  // Resumo do último cron + métricas 24h/7d (carregado no mount + após cada sync)
  const [resumoCron, setResumoCron] = useState<{
    ultima_sincronizacao: string | null;
    janela_24h: { importadas: number; triadas: number; descartadas: number; aprovadas: number; analises_dwight: number; custo_usd: number; modelo: string | null };
    janela_7d: { importadas: number; triadas: number; descartadas: number };
  } | null>(null);
  // Tracking de análises pendentes pós-disparo. Map<oportunidade_id, dispatchTs>
  // — quando o polling detecta uma análise mais nova que o dispatchTs, auto-abre
  // o ModalDecisao. Vive em memória (perde no reload da página, ok).
  const [analisesPendentes, setAnalisesPendentes] = useState<Record<number, number>>({});
  // LIC-FIT (2026-06-09) — seleção múltipla pra descarte/exclusão em lote
  const [selecionadas, setSelecionadas] = useState<number[]>([]);
  // 01/09/2026 — "Arquivar vencidas": prévia (dry-run) → confirmação → desfazer
  const [modalVencidas, setModalVencidas] = useState<
    { etapa: 'previa'; previa?: any } | { etapa: 'resultado'; resultado: any } | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [aplicandoVencidas, setAplicandoVencidas] = useState(false);
  // Radar (21/09/2026) — id da linha cujo botão 📡 está em curso.
  const [radarEmCurso, setRadarEmCurso] = useState<number | null>(null);
  // Portão do servidor (05/10/2026, handlers/licitacoes.js PORTAO_LICITACAO_POR_ACAO):
  // quem só consulta (coordenador, Daniele) vê a lista sem os botões que o
  // servidor recusaria. Falha ao carregar a matriz não vira "não pode".
  const { pode, erro: permsErro } = usePerms();
  const podeTriar = permsErro || pode('comercial.licitacoes.oportunidades.triar');
  const podeExcluir = permsErro || pode('comercial.licitacoes.oportunidades.excluir');
  const podeSincronizar = permsErro || pode('comercial.licitacoes.oportunidades.sincronizar_portais');
  const podeAnalisar = permsErro || pode('comercial.licitacoes.oportunidades.analise_profunda');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => v && params.set(k, v));
      const r = await axios.get(`${API_URL}/comercial/licitacoes?${params}`,
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setLicitacoes(Array.isArray(data) ? data : []);
    } catch { setLicitacoes([]); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarStatusTri = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'status_triagem' }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setStatusTri(d);
    } catch {}
  }, []);
  useEffect(() => { carregarStatusTri(); }, [carregarStatusTri, licitacoes.length]);

  const carregarResumoCron = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'resumo_cron' }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResumoCron(d);
    } catch {}
  }, []);
  useEffect(() => { carregarResumoCron(); }, [carregarResumoCron, licitacoes.length]);

  // L14b (2026-06-03) — count global de alteracoes pendentes
  const carregarPendentes = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'listar_eventos_pendentes', limit: 1 },
        { headers: { Authorization: token } });
      const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
      setPendentesTotal(d?.total || 0);
    } catch { setPendentesTotal(0); }
  }, []);
  useEffect(() => { carregarPendentes(); }, [carregarPendentes, licitacoes.length]);
  useEffect(() => {
    if (!statusTri || statusTri.pendentes === 0 || !triando) return;
    const t = setInterval(() => { carregarStatusTri(); carregar(); }, 15000);
    return () => clearInterval(t);
  }, [statusTri, triando, carregarStatusTri, carregar]);

  // ── Deep-link: abrir UMA oportunidade ao chegar de fora ────────────────
  // 🚨 Isto faltava, e fazia dois caminhos da entrega de 21/09 mentirem: o
  // botão "Abrir a oportunidade" do e-mail do radar (que manda
  // ?oportunidade=<id>) e a notificação do sino (que grava o id no
  // sessionStorage pela ponte do App.tsx) chegavam nesta tela e paravam na
  // LISTA — ninguém lia o id. A pessoa recebia "fim de prazo do edital X" e
  // tinha que procurar o X na mão.
  //
  // Duas fontes porque são dois caminhos: o e-mail só carrega URL, e o sino
  // só carrega sessionStorage (App.tsx navega por PAGE_ID_TO_PATH, sem query).
  // A chave é consumida na leitura para não reabrir o modal a cada render.
  //
  // 🚨 24/09/2026: a trava era um booleano "já fiz" — só a PRIMEIRA chegada
  // abria a ficha. Quem já estava nesta aba e clicava num sino de licitação
  // (o App.tsx grava o id e navega para /comercial/licitacoes, a aba não é
  // remontada) ficava na lista, com o id esquecido no sessionStorage. Agora o
  // efeito roda a cada navegação (`location.key`) e as DUAS fontes são
  // consumidas na leitura: a chave do sino é apagada e o `?oportunidade=` sai
  // da URL (replace). Sem tirar o parâmetro, trocar de aba no hub e voltar —
  // o HubAbas preserva a query — reabria a ficha a cada ida e volta.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    let alvo: number | null = null;
    try {
      const sp = new URLSearchParams(location.search);
      const daUrl = sp.get('oportunidade');
      const doSino = sessionStorage.getItem('licitacoes_abrir_oportunidade_id');
      if (doSino) sessionStorage.removeItem('licitacoes_abrir_oportunidade_id');
      // A URL ganha: é o clique que a pessoa acabou de dar (a chave do sino
      // pode ter sobrado de uma navegação que saiu antes de a lista carregar).
      alvo = Number(daUrl || doSino) || null;
      if (daUrl) {
        sp.delete('oportunidade');
        const resto = sp.toString();
        navigate({ pathname: location.pathname, search: resto ? `?${resto}` : '' }, { replace: true });
      }
    } catch { /* sessionStorage bloqueado: o deep-link é conveniência, não requisito */ }
    if (!alvo) return;
    const naLista = licitacoes.find(l => l.id === alvo);
    // Link antigo apontando para uma gêmea juntada: abre a PRINCIPAL (as ações
    // da janela gravam no id aberto).
    if (naLista?.unificada_em) alvo = naLista.unificada_em;
    else if (naLista) { setAberta(naLista); return; }
    // Não veio na página atual (filtro, arquivada, passou do limite): busca a
    // ficha direto, senão o link do e-mail morre justamente no edital vencendo.
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/licitacoes?action=detalhe&id=${alvo}`,
          { headers: { Authorization: token } });
        const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
        if (d?.id) setAberta(d as Licitacao);
      } catch {
        message.warning('Não consegui abrir essa licitação — ela pode ter sido excluída.');
      }
    })();
  }, [location.key, location.search, location.pathname, loading, licitacoes, message, navigate]);

  // L15.5 (2026-06-03) — Descarte rápido (soft delete) direto da lista.
  // Sem modal cheio — só Popconfirm. Mantém histórico (vs excluir definitivo).
  async function descartarOportunidade(l: Licitacao) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'descartar_oportunidade', id: l.id },
        { headers: { Authorization: token } });
      message.success(`Descartada: ${l.numero_edital || l.id}`);
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Radar (21/09/2026) — "acompanhar este edital" direto da lista.
  // Um clique coloca no radar com a antecedência padrão (5 dias) e o aviso
  // indo para quem clicou; o ajuste fino (data, destinatário, anotações) é
  // na aba Radar. Tirar do radar NÃO apaga as anotações — elas voltam se o
  // edital for colocado de novo.
  //
  // 🚨 A data não é escolhida aqui. O radar usa o prazo da FONTE
  // (`prazoEfetivo()` no servidor), e por isso um edital prorrogado ajusta o
  // aviso sozinho. Editais sem prazo nenhum na fonte — são 684 — o servidor
  // recusa, e a mensagem manda informar a data em Meu Radar.
  //
  // 🚨 Remover é por `radar_id`, não por `l.id`: o radar é pessoal desde
  // 22/09, e a MESMA oportunidade pode estar no radar de várias pessoas.
  async function alternarRadar(l: Licitacao) {
    setRadarEmCurso(l.id);
    try {
      if (l.no_radar && l.radar_id) {
        await radarApi.remover(l.radar_id);
        message.success(`Fora do seu radar: ${l.numero_edital || l.id}`);
        setLicitacoes(prev => prev.map(x =>
          (x.id === l.id ? { ...x, no_radar: false, radar_id: null } : x)));
      } else {
        const novo = await radarApi.acompanharLicitacao(l.id);
        // Desde 05/10/2026 o edital posto no radar entra no pipeline junto
        // (Avaliando) — o toast diz o que aconteceu lá.
        message.success(`No seu radar: ${l.numero_edital || l.id} — aviso ${DIAS_ANTECEDENCIA_PADRAO} dias antes do fim do prazo${textoDoPipeline(novo)}`);
        // Atualiza só esta linha: recarregar a lista inteira reordena a tabela
        // debaixo do cursor de quem acabou de clicar.
        setLicitacoes(prev => prev.map(x =>
          (x.id === l.id ? {
            ...x, no_radar: true, radar_id: novo.id,
            ...(novo.no_pipeline === 'entrou' ? {
              status_interno: novo.status_interno || 'avaliando', arquivada_em: null,
              responsavel_email: novo.pipeline_responsavel ?? x.responsavel_email,
            } : {}),
          } : x)));
      }
    } catch (e: any) {
      message.error(e?.response?.data?.erro || e?.response?.data?.error || e.message);
    } finally {
      setRadarEmCurso(null);
    }
  }

  // LIC-FIT (2026-06-09) — operações em lote sobre a seleção múltipla
  async function descartarLote() {
    if (!selecionadas.length) return;
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'descartar_oportunidades_lote', ids: selecionadas },
        { headers: { Authorization: token }, timeout: 30000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`${d.descartadas} oportunidade(s) descartada(s)`);
      setSelecionadas([]);
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function excluirLote() {
    if (!selecionadas.length) return;
    modal.confirm({
      title: `Excluir ${selecionadas.length} oportunidade(s) definitivamente?`,
      content: (
        <div style={{ fontSize: 12 }}>
          Ação <strong style={{ color: '#B91626' }}>permanente</strong> — apaga registros,
          anexos e análises. Oportunidades com proposta vinculada são <strong>puladas</strong>
          {' '}(exclua essas individualmente pra decidir o destino da proposta).
        </div>
      ),
      okText: 'Excluir tudo', okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'excluir_oportunidades_lote', ids: selecionadas },
            { headers: { Authorization: token }, timeout: 60000 });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          const puladas = d.puladas_com_proposta?.length || 0;
          message.success(`${d.excluidas} excluída(s)${puladas ? `, ${puladas} pulada(s) por ter proposta` : ''}`);
          setSelecionadas([]);
          carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  // 2026-05-29 — Excluir oportunidade definitivamente. FK CASCADE no DB cuida
  // de analises + anexos. Se tem proposta vinculada, pergunta se cancela junto.
  function excluirOportunidade(l: Licitacao) {
    let cancelarProposta = false;
    modal.confirm({
      title: `Excluir oportunidade "${l.numero_edital || l.id}"?`,
      content: (
        <div style={{ fontSize: 12 }}>
          Ação <strong style={{ color: '#B91626' }}>permanente</strong>. Apaga:
          <ul style={{ marginTop: 6, paddingLeft: 20 }}>
            <li>Registro da oportunidade</li>
            <li>Anexos baixados (PDFs do edital)</li>
            <li>Análises do Dwight (histórico)</li>
          </ul>
          {l.proposta_id ? (
            <div style={{ marginTop: 10, padding: 8, background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 4 }}>
              <strong>Proposta #{l.proposta_id}</strong> está vinculada. Por padrão é mantida no Pipeline.
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer' }}>
                <input type="checkbox" onChange={e => { cancelarProposta = e.target.checked; }} />
                <span>Marcar a proposta como <strong>cancelada</strong> também</span>
              </label>
            </div>
          ) : null}
        </div>
      ),
      okText: 'Excluir', okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      width: 520,
      onOk: async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'excluir_oportunidade', id: l.id, excluir_proposta_tambem: cancelarProposta },
            { headers: { Authorization: token }, timeout: 30000 });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          message.success(`Oportunidade excluída${d.proposta_cancelada ? ` (proposta #${d.proposta_vinculada} cancelada)` : ''}.`);
          setAberta(null);
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  // O texto abaixo prometia relatório de habilitação técnica/financeira/
  // jurídica por ~R$ 1,50. O que roda de verdade é a análise BREVE (Haiku),
  // que lê o edital e devolve um parecer com recomendação — o cruzamento com
  // o acervo de documentos da empresa é a análise profunda, que ainda não
  // existe. Prometer o que não entrega faz a pessoa desconfiar do parecer.
  function analisarProfundo(l: Licitacao) {
    modal.confirm({
      title: `Análise breve do edital "${l.numero_edital || l.id}"?`,
      content: (
        <div style={{ fontSize: 12 }}>
          O Dwight baixa o edital e os anexos, lê com o Claude Haiku e gera um parecer
          com recomendação (vale aprofundar / atenção / não recomendado).
          <br /><br />
          <strong>Custo:</strong> ~R$ 0,30 por licitação.<br />
          <strong>Tempo:</strong> 30-90 s.<br /><br />
          <em>Quando terminar, abre um painel com a decisão sugerida (descartar ou gerar proposta).
          O cruzamento com o acervo de documentos da empresa vem na análise profunda (próxima etapa).</em>
        </div>
      ),
      okText: 'Iniciar análise', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'analise_profunda', oportunidade_id: l.id },
            { headers: { Authorization: token }, timeout: 25000 });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          message.success(d.mensagem || 'Análise iniciada. Painel abre quando concluir.');
          // Marca timestamp do disparo. O polling no useEffect abaixo detecta a
          // análise nova (criado_em > dispatchTs) e auto-abre o ModalDecisao.
          setAnalisesPendentes(prev => ({ ...prev, [l.id]: Date.now() }));
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  // Polling: enquanto houver análises pendentes, checa o detalhe da licitação
  // a cada 10s pra ver se a análise terminou (analise mais nova que dispatchTs).
  // Cancelamento automático após 5 min (analise normal leva 30-90s).
  useEffect(() => {
    const pendentes = Object.entries(analisesPendentes);
    if (pendentes.length === 0) return;
    const interval = setInterval(async () => {
      const token = await getToken();
      for (const [oportIdStr, dispatchTs] of pendentes) {
        const oportId = parseInt(oportIdStr, 10);
        // timeout 5min
        if (Date.now() - dispatchTs > 5 * 60 * 1000) {
          setAnalisesPendentes(prev => {
            const next = { ...prev }; delete next[oportId]; return next;
          });
          continue;
        }
        try {
          const r = await axios.get(`${API_URL}/comercial/licitacoes?action=detalhe&id=${oportId}`,
            { headers: { Authorization: token } });
          const data = r.data.body ? JSON.parse(r.data.body) : r.data;
          const analises = Array.isArray(data?.analises) ? data.analises : [];
          // Análise breve com criado_em DEPOIS do dispatchTs = a que acabou de rodar
          const novaAnalise = analises.find((a: any) =>
            a.tipo === 'analise_breve' && a.conteudo
            && new Date(a.criado_em).getTime() > dispatchTs);
          if (novaAnalise) {
            // Auto-abre o ModalDecisao
            setDecisaoOpen({ lic: data, analises });
            setAnalisesPendentes(prev => {
              const next = { ...prev }; delete next[oportId]; return next;
            });
            // Refresca lista pra mostrar fit atualizado
            carregar();
          }
        } catch {
          // silencia erros transitórios, próximo tick tenta de novo
        }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [analisesPendentes, carregar]);

  // 02/10/2026: "Gerar proposta" virou "Avaliar". A licitação entra no pipeline de
  // licitações em Avaliando, com responsável (quem clicou), e a tela do processo
  // abre lá. O negócio gêmeo (comercial.propostas) só nasce na etapa Proposta —
  // antes, cada clique criava um negócio no funil privado que ninguém perseguia.
  async function gerarProposta(l: Licitacao) {
    const abrir = (id: number) => {
      setAberta(null); setDecisaoOpen(null);
      navigate(`/comercial/licitacoes?aba=pipeline&licitacao=${id}`);
    };
    if (!['novo', 'em_analise', 'descartado'].includes(l.status_interno) && !l.arquivada_em) { abrir(l.id); return; }
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'lic_avaliar', id: l.id }, { headers: { Authorization: token }, timeout: 25000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`${l.numero_edital || 'Licitação'} entrou no pipeline em Avaliando.`);
      abrir(d?.id || l.id);
    } catch (e: any) {
      message.error('Não consegui avaliar: ' + (e.response?.data?.error || e.message));
    }
  }

  async function triar() {
    setTriando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'triar_pendentes', limite: 50 },
        { headers: { Authorization: token }, timeout: 25000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.status === 'sem_pendencias') {
        message.info(d.mensagem); setTriando(false); return;
      }
      setTimeout(() => setTriando(false), 300000);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
      setTriando(false);
    }
  }

  function sincronizar() {
    modal.confirm({
      title: 'Sincronizar oportunidades do PNCP?',
      content: 'Vou buscar editais com proposta aberta, filtrar pelas palavras-chave/UFs configuradas e importar. Roda em segundo plano (60-90s).',
      okText: 'Sincronizar', cancelText: 'Cancelar',
      onOk: async () => {
        setSincronizando(true);
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'sync' }, { headers: { Authorization: token }, timeout: 25000 });
          const inicio = Date.now();
          const interval = setInterval(async () => {
            await carregar();
            try {
              const tk = await getToken();
              const cr = await axios.get(`${API_URL}/comercial/licitacoes?action=config`,
                { headers: { Authorization: tk } });
              const cdata = cr.data.body ? JSON.parse(cr.data.body) : cr.data;
              const ts = cdata.ultima_sincronizacao ? new Date(cdata.ultima_sincronizacao).getTime() : 0;
              if (ts >= inicio - 5000) { clearInterval(interval); setSincronizando(false); return; }
            } catch {}
            if (Date.now() - inicio > 120000) { clearInterval(interval); setSincronizando(false); }
          }, 15000);
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
          setSincronizando(false);
        }
      },
    });
  }

  function sincronizarConlicitacao() {
    modal.confirm({
      title: 'Sincronizar ConLicitação?',
      content: 'Busca boletins mais recentes dos filtros cadastrados na sua conta. Roda em segundo plano (60-90s).',
      okText: 'Sincronizar', cancelText: 'Cancelar',
      onOk: async () => {
        setSincronizandoConlic(true);
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'conlic_sync' }, { headers: { Authorization: token }, timeout: 25000 });
          const sizeAntes = licitacoes.length;
          const inicio = Date.now();
          const interval = setInterval(async () => {
            await carregar();
            if (licitacoes.length > sizeAntes || Date.now() - inicio > 120000) {
              clearInterval(interval); setSincronizandoConlic(false);
            }
          }, 15000);
        } catch (e: any) {
          const msg = e.response?.data?.error || e.message;
          if (msg.includes('Token ConLicitacao não configurado')) {
            message.warning('Token do ConLicitação não configurado. Solicite ao 0800 11 14133 e atualize o secret.');
          } else {
            message.error('Erro ConLicitação: ' + msg);
          }
          setSincronizandoConlic(false);
        }
      },
    });
  }

  // ── Buscar o mês que ficou para trás (ConLicitação) ──────────────────
  // O sync normal pega só os boletins mais recentes. Depois de uma parada da
  // fonte (a de 06/06→01/09/2026 deixou ~165 edições para trás), o acervo do
  // período só volta pedindo a JANELA. A busca roda em etapas e se rechama
  // sozinha até fechar — não precisa clicar de novo.
  function buscarMesAnteriorConlic() {
    const ate = new Date();
    ate.setDate(ate.getDate() - 1);
    const desde = new Date(ate);
    desde.setDate(desde.getDate() - 29);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    modal.confirm({
      title: 'Buscar o último mês do ConLicitação?',
      content: (
        <>
          <div>Vai pedir os boletins de <strong>{fmtDataBr(iso(desde))}</strong> a <strong>{fmtDataBr(iso(ate))}</strong> e importar o que ainda não está aqui.</div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
            Boletim já importado não é buscado de novo. São 3 boletins por dia útil e cada um leva ~7 s,
            então um mês leva alguns minutos — a busca continua sozinha em segundo plano.
          </div>
        </>
      ),
      okText: 'Buscar', cancelText: 'Cancelar', width: 520,
      onOk: async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'conlic_sync', desde: iso(desde), ate: iso(ate) },
            { headers: { Authorization: token }, timeout: 25000 });
          const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
          message.success(d?.mensagem || 'Busca retroativa iniciada.');
        } catch (e: any) {
          message.error('Erro na busca retroativa: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  // ── Arquivar vencidas sem participação ───────────────────────────────
  // Sempre SIMULA primeiro: mostra a contagem e uma amostra, e só escreve
  // depois da confirmação. Arquivar é reversível — o modal mostra como.
  async function abrirArquivarVencidas() {
    setCarregandoPrevia(true);
    setModalVencidas({ etapa: 'previa' });
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'arquivar_vencidas' },
        { headers: { Authorization: token }, timeout: 60000 });
      const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
      if (d?.erro) { message.error(d.erro); setModalVencidas(null); return; }
      setModalVencidas({ etapa: 'previa', previa: d });
    } catch (e: any) {
      message.error('Não deu para contar as vencidas: ' + (e.response?.data?.error || e.message));
      setModalVencidas(null);
    } finally { setCarregandoPrevia(false); }
  }

  async function confirmarArquivarVencidas() {
    setAplicandoVencidas(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'arquivar_vencidas', confirmar: true },
        { headers: { Authorization: token }, timeout: 120000 });
      const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
      if (d?.erro) { message.error(d.erro); return; }
      setModalVencidas({ etapa: 'resultado', resultado: d });
      carregar();
    } catch (e: any) {
      message.error('Erro ao arquivar: ' + (e.response?.data?.error || e.message));
    } finally { setAplicandoVencidas(false); }
  }

  async function desfazerArquivarVencidas(desfazer: any) {
    setAplicandoVencidas(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'desarquivar_lote', ...desfazer },
        { headers: { Authorization: token }, timeout: 120000 });
      const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
      message.success(`${d?.desarquivadas ?? 0} oportunidade(s) de volta na lista.`);
      setModalVencidas(null);
      carregar();
    } catch (e: any) {
      message.error('Erro ao desfazer: ' + (e.response?.data?.error || e.message));
    } finally { setAplicandoVencidas(false); }
  }

  const porStatus = useMemo(() => {
    const m: Record<string, number> = {};
    for (const l of licitacoes) m[l.status_interno] = (m[l.status_interno] || 0) + 1;
    return m;
  }, [licitacoes]);

  // L13 (2026-06-02) — sorters em todas colunas exceto Objeto (textão).
  // Default: ordena por data_abertura ASC (mesma ordem do back-end).
  const cmpStr = (a: string | null | undefined, b: string | null | undefined) =>
    String(a || '').localeCompare(String(b || ''), 'pt-BR', { sensitivity: 'base' });
  const cmpNum = (a: number | null | undefined, b: number | null | undefined) =>
    (Number(a) || 0) - (Number(b) || 0);
  const cmpDate = (a: string | null | undefined, b: string | null | undefined) => {
    const da = a ? new Date(a).getTime() : 0;
    const db = b ? new Date(b).getTime() : 0;
    return da - db;
  };

  const cols: ColumnsType<Licitacao> = [
    {
      title: 'Status', dataIndex: 'status_interno', key: 'st', width: 110,
      sorter: (a, b) => cmpStr(a.status_interno, b.status_interno),
      render: (s) => {
        const c = STATUS_INTERNO_CFG[s] || STATUS_INTERNO_CFG.novo;
        return <Tag color={c.tagColor}>{c.label}</Tag>;
      },
    },
    {
      title: 'Edital / Órgão', dataIndex: 'numero_edital', key: 'ed', width: 220,
      sorter: (a, b) => cmpStr(a.numero_edital || a.orgao, b.numero_edital || b.orgao),
      render: (_, l) => (
        <div>
          <Space size={4}>
            <Text strong style={{ fontSize: 12 }}>{l.numero_edital || '—'}</Text>
            {(l.fonte === 'conlicitacao' || l.gemeas?.some(g => g.fonte === 'conlicitacao')) && <Tag color="purple" style={{ fontSize: 9 }}>CONLIC</Tag>}
            {/* A mesma licitação pelas duas fontes (07/10/2026). */}
            {!!l.gemeas?.length && l.fonte !== 'conlicitacao' && <Tag color="blue" style={{ fontSize: 9 }}>PNCP</Tag>}
            {l.unificada_em && (
              <Tooltip title="A mesma licitação veio pela outra fonte; esta linha foi juntada à principal (clique para abrir).">
                <Tag style={{ fontSize: 9 }}>juntada à #{l.unificada_em}</Tag>
              </Tooltip>
            )}
          </Space>
          <div><Text type="secondary" style={{ fontSize: 10 }}>{l.orgao || '—'}</Text></div>
        </div>
      ),
    },
    {
      title: 'Local', key: 'loc', width: 100,
      sorter: (a, b) => cmpStr(`${a.uf || ''}-${a.cidade || ''}`, `${b.uf || ''}-${b.cidade || ''}`),
      render: (_, l) => (
        <Text style={{ fontSize: 11 }}>
          {l.cidade ? `${l.cidade}/` : ''}{l.uf || '—'}
        </Text>
      ),
    },
    {
      title: 'Área', dataIndex: 'area_tecnica', key: 'at', width: 120,
      sorter: (a, b) => cmpStr(a.area_tecnica, b.area_tecnica),
      render: (a, l) => !l.triada_em
        ? <Text type="secondary" italic style={{ fontSize: 10 }}>(não triada)</Text>
        : a && a !== 'Outros'
          ? (
            <Tooltip title={a}>
              <Tag color="blue" style={{
                maxWidth: 108, display: 'inline-block',
                overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', verticalAlign: 'middle',
              }}>{a}</Tag>
            </Tooltip>
          )
          : <Text type="secondary" style={{ fontSize: 11 }}>{a || '—'}</Text>,
    },
    // L14b (2026-06-03) — alterações pendentes
    {
      title: 'Alteração', key: 'alt', width: 130,
      sorter: (a, b) => cmpDate(a.ultima_alteracao_em, b.ultima_alteracao_em),
      render: (_, l) => {
        const n = l.eventos_unack_count || 0;
        if (!l.ultima_alteracao_em && n === 0) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        const d = l.ultima_alteracao_em ? new Date(l.ultima_alteracao_em) : null;
        const fmt = d ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
        return (
          <Tooltip title={l.ultimo_evento_resumo || ''}>
            <div>
              {n > 0 && (
                <Badge count={n} style={{ backgroundColor: '#B91626', marginRight: 4 }} />
              )}
              <Text style={{ fontSize: 10, color: n > 0 ? '#B91626' : '#666' }}>{fmt}</Text>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Objeto', dataIndex: 'objeto', key: 'ob', ellipsis: { showTitle: false },
      render: (v, row) => {
        const pos = row.palavras_match || [];
        const neg = row.palavras_match_negativas || [];
        return (
          <div>
            <Tooltip title={v || '—'} placement="topLeft">
              <div style={{
                fontSize: 11, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal', lineHeight: '1.3',
              }}>{tituloDoObjeto(v) || v || '—'}</div>
            </Tooltip>
            {(pos.length > 0 || neg.length > 0) && (
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {pos.map((kw) => (
                  <Tag key={`p-${kw}`} color="green" style={{ fontSize: 9, padding: '0 4px', margin: 0, lineHeight: '14px' }}>
                    {kw}
                  </Tag>
                ))}
                {neg.map((kw) => (
                  <Tooltip key={`n-${kw}`} title="Palavra negativa que casou — em tese não devia ter passado">
                    <Tag color="red" style={{ fontSize: 9, padding: '0 4px', margin: 0, lineHeight: '14px' }}>
                      ⚠ {kw}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      // Mostra o PRAZO PARA PROPOSTA (`prazo_efetivo`, calculado no SQL) e não
      // mais a `data_abertura` crua: no PNCP a abertura é o INÍCIO da janela de
      // propostas, então uma licitação "aberta ontem" aparecia como vencida.
      // Sem `prazo_efetivo` (linha de cache antigo) cai na abertura, como antes.
      title: (
        <Tooltip title="Prazo para envio da proposta. Sem esse dado na fonte, mostramos a data de abertura.">
          <span>Prazo</span>
        </Tooltip>
      ),
      dataIndex: 'data_abertura', key: 'ab', width: 160,
      // Sem isto o antd embrulha o cabeçalho no tooltip dele ("clique para
      // ordenar") e o nosso, que é o que explica a coluna, aparece por cima.
      showSorterTooltip: false,
      sorter: (a, b) => cmpDate(prazoEfetivoCliente(a), prazoEfetivoCliente(b)),
      defaultSortOrder: 'ascend' as const,
      render: (_v, l) => {
        // Licitação do quadro com a proposta já enviada (05/10/2026): o prazo
        // dela não é mais alerta — mesma régua do pipeline (propostaJaFoi).
        if (ehAberta(l.status_interno) && propostaJaFoi(l.status_interno, l.proposta_enviada_em)) {
          return (
            <div style={{ fontSize: 11, lineHeight: '14px' }}>
              <Text style={{ color: '#0a7f4a', fontWeight: 600 }}>✓ proposta enviada</Text>
              {l.proposta_enviada_em && <div style={{ fontSize: 9, color: '#888' }}>{fmtDataHoraCurta(l.proposta_enviada_em)}</div>}
            </div>
          );
        }
        const prazo = classificarPrazoEdital(prazoEfetivoCliente(l));
        if (!prazo) {
          // L15.4 (2026-06-03) — sem data: mostra a situação do ConLic pra
          // contextualizar (NOVA/URGEN/PRO sem prazo definido; SUS suspensa).
          const sit = l.situacao_unificada
            ? situacaoLegivel(l.situacao_unificada, l.situacao_unificada_fonte)
            : situacaoLegivel(l.situacao, l.fonte);
          if (sit && (sit.semData || sit.conhecida)) {
            return (
              <Tooltip title={sit.tip}>
                <Tag color={sit.cor} style={{ fontSize: 9 }}>{sit.semData || sit.txt.toLowerCase()}</Tag>
              </Tooltip>
            );
          }
          return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        }
        // L16a (2026-06-03) — contador colorido com dias faltantes (a régua
        // agora mora em prazo.ts, compartilhada com o modal).
        return (
          <div style={{ fontSize: 11, lineHeight: '14px' }}>
            <Text style={{ color: prazo.cor, fontWeight: prazo.bold ? 600 : 400 }}>
              {prazo.icone}{prazo.rotulo}
            </Text>
            <div style={{ fontSize: 9, color: '#888' }}>
              {fmtDataHoraCurta(prazo.data)}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Valor', dataIndex: 'valor_estimado', key: 've', width: 130, align: 'right',
      sorter: (a, b) => cmpNum(valorDaLinha(a), valorDaLinha(b)),
      // O mais atualizado entre as fontes, quando a licitação veio pelas duas.
      render: (_, l) => {
        const v = valorDaLinha(l);
        return v != null
          ? <Text style={{ fontSize: 11 }}>R$ {v.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</Text>
          : '—';
      },
    },
    {
      title: 'Fit técnico', dataIndex: 'fit_score', key: 'fs', width: 85, align: 'center',
      sorter: (a, b) => cmpNum(a.fit_score, b.fit_score),
      render: (_, l) => {
        const fit = fitTecnicoUI(l);
        return <Tooltip title={fit.dica}><Tag color={fit.tagColor}>{fit.label}</Tag></Tooltip>;
      },
    },
    // Radar (21/09/2026) — "acompanhar este edital". Fica ao lado do
    // descarte de propósito: são as duas decisões que a pessoa toma olhando
    // a linha ("quero" / "não quero"). Doc: docs/licitacoes-radar.md.
    {
      title: '', key: 'radar', width: 44, align: 'center',
      // Gêmea juntada (só aparece em "Mostrar arquivadas"): sem radar nem
      // descarte próprios — a decisão é na principal, que a linha abre.
      render: (_, l) => l.unificada_em ? null : (
        <Se permissao="radar.meu.gerenciar">
          <Tooltip title={l.no_radar
            ? 'Já está no seu radar — abra Comercial › Meu Radar para ver prazo, aviso e anotações'
            : 'Adicionar ao radar: entra no calendário e avisa por e-mail antes do fim do prazo'}>
            <Button
              size="small" type="text"
              loading={radarEmCurso === l.id}
              style={{ color: l.no_radar ? '#020D7F' : '#bbb' }}
              icon={<RadarChartOutlined />}
              onClick={(e) => { e.stopPropagation(); alternarRadar(l); }}
            />
          </Tooltip>
        </Se>
      ),
    },
    // L15.5 (2026-06-03) — descarte rápido (soft delete)
    {
      title: '', key: 'acoes', width: 50, align: 'center',
      render: (_, l) => (!podeTriar || l.unificada_em) ? null : (
        <Popconfirm
          title="Descartar?"
          description={<div style={{ fontSize: 11, maxWidth: 220 }}>
            Marca como descartada e arquiva. Reversível via "Mostrar arquivadas".
          </div>}
          okText="Descartar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true, size: 'small' }}
          onConfirm={(e) => { e?.stopPropagation(); descartarOportunidade(l); }}
          onCancel={(e) => e?.stopPropagation()}>
          <Tooltip title="Descartar (não interessa)">
            <Button size="small" type="text" danger
              icon={<StopOutlined />}
              onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  // Resumo do último cron — formatação inline
  function fmtCusto(usd: number) {
    const brl = usd * 5;  // BRL ≈ USD * 5
    return `US$ ${usd.toFixed(2)} (~R$ ${brl.toFixed(2)})`;
  }
  function fmtModeloShort(modeloFull: string | null) {
    if (!modeloFull) return '—';
    // ex: "anthropic.claude-haiku-4-5-20251020" → "Haiku 4.5"
    const m = modeloFull.match(/(haiku|sonnet|opus)-(\d+(?:-\d+)?)/i);
    if (m) return `${m[1].charAt(0).toUpperCase() + m[1].slice(1)} ${m[2].replace('-', '.')}`;
    return modeloFull.split('.').pop() || modeloFull;
  }
  function fmtTempoRel(iso: string | null) {
    if (!iso) return 'nunca';
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 60) return `há ${diffMin}min`;
    if (diffMin < 1440) return `há ${Math.floor(diffMin / 60)}h`;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <>
      {/* Resumo do último cron — fica no topo, sempre visível */}
      {resumoCron && (
        <Card size="small" style={{ marginBottom: 10, background: '#F0F5FF', border: '1px solid #BAD8FF' }}
          styles={{ body: { padding: '8px 12px' } }}>
          <Flex gap={20} align="center" wrap="wrap" style={{ fontSize: 11 }}>
            <Space size={4}>
              <ClockCircleOutlined style={{ color: '#0930D0' }} />
              <Text strong>Última sync:</Text>
              <Text>{fmtTempoRel(resumoCron.ultima_sincronizacao)}</Text>
            </Space>
            <Space size={4}>
              <Text strong>Últimas 24h:</Text>
              <Tag color="blue">{resumoCron.janela_24h.importadas} importadas</Tag>
              <Tag color="purple">{resumoCron.janela_24h.triadas} triadas</Tag>
              <Tag color="red">{resumoCron.janela_24h.descartadas} descartadas</Tag>
              <Tag color="green">{resumoCron.janela_24h.aprovadas} em análise</Tag>
            </Space>
            {resumoCron.janela_24h.analises_dwight > 0 && (
              <Space size={4}>
                <DollarOutlined style={{ color: '#0a7f4a' }} />
                <Text strong>Custo Dwight 24h:</Text>
                <Text>{fmtCusto(resumoCron.janela_24h.custo_usd)}</Text>
                <Text type="secondary">· {fmtModeloShort(resumoCron.janela_24h.modelo)}</Text>
              </Space>
            )}
            <Space size={4} style={{ marginLeft: 'auto' }}>
              <Text type="secondary" style={{ fontSize: 10 }}>
                7d: {resumoCron.janela_7d.importadas} novas · {resumoCron.janela_7d.descartadas} descartadas
              </Text>
            </Space>
          </Flex>
        </Card>
      )}

      <Card size="small" style={{ marginBottom: 14, background: '#FAFBFF' }} styles={{ body: { padding: 10 } }}>
        <Flex gap={8} align="center" wrap="wrap">
          <Input value={filtros.busca} onChange={e => setFiltros({ ...filtros, busca: e.target.value })}
            placeholder="Buscar em objeto ou órgão..."
            prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small"
            style={{ flex: 1, minWidth: 220 }} allowClear />
          <Select value={filtros.status_interno || undefined}
            onChange={(v) => setFiltros({ ...filtros, status_interno: v || '' })}
            placeholder="Todos status" allowClear size="small" style={{ width: 160 }}
            options={Object.entries(STATUS_INTERNO_CFG).map(([k, v]) => ({
              label: `${v.label}${porStatus[k] ? ` (${porStatus[k]})` : ''}`, value: k,
            }))} />
          <Input value={filtros.uf}
            onChange={e => setFiltros({ ...filtros, uf: e.target.value.toUpperCase().slice(0, 2) })}
            placeholder="UF" size="small" style={{ width: 70 }} />
          <Input value={filtros.modalidade}
            onChange={e => setFiltros({ ...filtros, modalidade: e.target.value })}
            placeholder="Modalidade" size="small" style={{ width: 130 }} />
          <InputNumber value={filtros.fit_score_min ? parseInt(filtros.fit_score_min) : undefined}
            onChange={(v) => setFiltros({ ...filtros, fit_score_min: v ? String(v) : '' })}
            placeholder="Fit ≥" min={0} max={100} size="small" style={{ width: 80 }} />
          {podeSincronizar && (<>
            <Button size="small" icon={<ReloadOutlined spin={sincronizando} />} loading={sincronizando}
              onClick={sincronizar} style={{ color: '#0930D0', borderColor: '#0930D0' }}>
              Sync PNCP
            </Button>
            <Button size="small" icon={<ReloadOutlined spin={sincronizandoConlic} />} loading={sincronizandoConlic}
              onClick={sincronizarConlicitacao} style={{ color: '#7C3AED', borderColor: '#7C3AED' }}
              title="Boletins do ConLicitação (fonte paga consolidada)">
              Sync ConLicitação
            </Button>
          </>)}
          {podeTriar && (
            <Button size="small" icon={<RobotOutlined />} loading={triando}
              disabled={!statusTri || statusTri.pendentes === 0}
              onClick={triar} style={{ color: '#7C3AED', borderColor: '#7C3AED' }}
              title="Dwight avalia licitações não-triadas (fit_score + área + status)">
              Triar Dwight{statusTri?.pendentes ? ` (${statusTri.pendentes})` : ''}
            </Button>
          )}
          {podeSincronizar && (
            <Button size="small" icon={<HistoryOutlined />}
              onClick={buscarMesAnteriorConlic}
              style={{ color: '#7C3AED', borderColor: '#7C3AED' }}
              title="Busca os boletins do ConLicitação dos últimos 30 dias que ainda não estão aqui (roda em etapas, em segundo plano)">
              Buscar mês anterior
            </Button>
          )}
          {/* 2026-05-29: botão "Arquivar descartadas" removido — descartar agora
              arquiva automaticamente via trigger no DB. */}
          {/* 01/09/2026 — arquiva o que já venceu e não virou proposta.
              Mostra a contagem ANTES de escrever; é reversível. */}
          <Se permissao="comercial.licitacoes.oportunidades.triar">
            <Button size="small" icon={<InboxOutlined />} loading={carregandoPrevia}
              onClick={abrirArquivarVencidas}
              style={{ color: '#B07500', borderColor: '#B07500' }}
              title="Arquiva editais cujo prazo de proposta já passou e que não viraram proposta. Mostra quantos são antes de fazer.">
              Arquivar vencidas
            </Button>
          </Se>
          <Checkbox checked={filtros.incluir_arquivadas === '1'}
            onChange={e => setFiltros({ ...filtros, incluir_arquivadas: e.target.checked ? '1' : '' })}>
            <Text style={{ fontSize: 11 }}>Mostrar arquivadas</Text>
          </Checkbox>
          <Checkbox checked={filtros.incluir_vencidas === '1'}
            onChange={e => setFiltros({ ...filtros, incluir_vencidas: e.target.checked ? '1' : '' })}
            title="Oportunidades em 'novo' ou 'em_analise' com data de abertura no passado">
            <Text style={{ fontSize: 11 }}>Mostrar vencidas</Text>
          </Checkbox>
          {/* L14b (2026-06-03) — filtro de alterações pendentes */}
          <Checkbox checked={filtros.apenas_com_alteracoes === '1'}
            onChange={e => setFiltros({ ...filtros, apenas_com_alteracoes: e.target.checked ? '1' : '' })}
            title="Oportunidades cujo último sync detectou mudança material (status, valor, data, objeto)">
            <Text style={{ fontSize: 11, color: '#B91626', fontWeight: 600 }}>
              <BellFilled style={{ marginRight: 4 }} />
              Com alterações
            </Text>
          </Checkbox>
        </Flex>
      </Card>

      {/* L14b (2026-06-03) — alerta de alterações pendentes globais */}
      {pendentesTotal > 0 && filtros.apenas_com_alteracoes !== '1' && (
        <Alert
          type="warning" showIcon
          style={{ marginBottom: 14, cursor: 'pointer' }}
          message={
            <Flex justify="space-between" align="center">
              <span>
                <strong>{pendentesTotal}</strong> {pendentesTotal === 1 ? 'alteração pendente' : 'alterações pendentes'} de revisão
              </span>
              <Button size="small" type="link" danger
                onClick={() => setFiltros({ ...filtros, apenas_com_alteracoes: '1' })}>
                Ver só essas
              </Button>
            </Flex>
          }
          description={
            <Text type="secondary" style={{ fontSize: 11 }}>
              Mudanças vindas dos boletins do ConLicitação (status, prazo, valor ou objeto) que ainda não foram revisadas.
              Clique numa linha pra ver a timeline da oportunidade.
            </Text>
          }
        />
      )}

      {/* LIC-FIT: barra de ações em lote (aparece quando há seleção) */}
      {selecionadas.length > 0 && (
        <Flex gap={8} align="center" style={{
          marginBottom: 8, padding: '6px 12px', background: '#FFF8E1',
          border: '1px solid #FFD54F', borderRadius: 6,
        }}>
          <Text strong style={{ fontSize: 12 }}>{selecionadas.length} selecionada(s)</Text>
          {podeTriar && (
            <Popconfirm title={`Descartar ${selecionadas.length} oportunidade(s)?`}
              description="Soft delete — reversível via 'Mostrar arquivadas'."
              okText="Descartar" cancelText="Cancelar" onConfirm={descartarLote}>
              <Button size="small" icon={<StopOutlined />}>Descartar selecionadas</Button>
            </Popconfirm>
          )}
          {podeExcluir && <Button size="small" danger onClick={excluirLote}>Excluir definitivamente</Button>}
          <Button size="small" type="link" onClick={() => setSelecionadas([])}>Limpar seleção</Button>
        </Flex>
      )}
      <Table
        rowKey="id" columns={cols} dataSource={licitacoes} loading={loading}
        size="small" pagination={{ pageSize: 50, showSizeChanger: true }}
        rowSelection={!(podeTriar || podeExcluir) ? undefined : {
          selectedRowKeys: selecionadas,
          onChange: (keys) => setSelecionadas(keys as number[]),
          // Checkbox não deve abrir o modal da linha
          getCheckboxProps: () => ({ onClick: (e: any) => e.stopPropagation() }),
        }}
        // Gêmea juntada (só aparece com "Mostrar arquivadas"): abre a PRINCIPAL —
        // as ações da janela gravam no id aberto, e o lugar delas é a principal.
        onRow={(record) => ({ onClick: () => setAberta(record.unificada_em ? { ...record, id: record.unificada_em } as Licitacao : record), style: { cursor: 'pointer' } })}
        locale={{ emptyText: (
          <Empty description={
            <Text style={{ fontSize: 12 }}>
              Nenhuma licitação cadastrada.<br />
              Configure palavras-chave/UFs na aba "Configurações" e clique em "Sync PNCP".
            </Text>
          } />
        ) }}
        // scroll x mínimo de 900px: em telas largas a tabela ocupa tudo;
        // em mobile/tablet aparece scroll horizontal sem quebrar layout.
        scroll={{ x: 900 }}
        tableLayout="fixed"
        style={{ width: '100%' }}
      />

      {aberta && (
        <ModalLicitacao licitacao={aberta} onClose={() => setAberta(null)}
          onSaved={() => { setAberta(null); carregar(); }}
          onAnalisar={podeAnalisar ? analisarProfundo : undefined}
          onGerarProposta={gerarProposta}
          onVerDecisao={(l, analises) => setDecisaoOpen({ lic: l, analises })}
          onExcluir={podeExcluir ? excluirOportunidade : undefined} />
      )}

      {decisaoOpen && (
        <ModalDecisao
          licitacao={decisaoOpen.lic}
          analises={decisaoOpen.analises}
          onClose={() => setDecisaoOpen(null)}
          onDescartado={() => carregar()}
          onGerarProposta={gerarProposta}
        />
      )}

      {/* ── Arquivar vencidas: conta primeiro, escreve depois ───────────── */}
      <Modal
        open={!!modalVencidas}
        onCancel={() => setModalVencidas(null)}
        title={modalVencidas?.etapa === 'resultado' ? 'Pronto' : 'Arquivar vencidas sem participação'}
        width="94vw"
        style={{ maxWidth: 900, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
        footer={
          modalVencidas?.etapa === 'resultado' ? [
            <Button key="desfazer" icon={<UndoOutlined />} loading={aplicandoVencidas}
              onClick={() => desfazerArquivarVencidas((modalVencidas as any).resultado?.desfazer || {})}>
              Desfazer
            </Button>,
            <Button key="fechar" type="primary" onClick={() => setModalVencidas(null)}>Fechar</Button>,
          ] : [
            <Button key="cancelar" onClick={() => setModalVencidas(null)}>Cancelar</Button>,
            <Button key="ok" danger type="primary" loading={aplicandoVencidas}
              disabled={!((modalVencidas as any)?.previa?.total > 0)}
              onClick={confirmarArquivarVencidas}>
              Arquivar {(modalVencidas as any)?.previa?.total ?? 0}
            </Button>,
          ]
        }
      >
        {modalVencidas?.etapa === 'resultado' ? (
          <>
            <Alert type="success" showIcon style={{ marginBottom: 12 }}
              message={`${(modalVencidas as any).resultado?.arquivadas ?? 0} oportunidade(s) arquivada(s)`}
              description={
                <Text style={{ fontSize: 12 }}>
                  Nada foi apagado. Para rever, marque <strong>“Mostrar arquivadas”</strong> na barra de filtros —
                  elas ficam com a marca <code>vencidas-sem-participacao</code>. O botão “Desfazer” aqui embaixo
                  devolve o lote inteiro para a lista.
                </Text>
              } />
          </>
        ) : !(modalVencidas as any)?.previa ? (
          // antd v6: `tip` no Spin só vale em modo aninhado/fullscreen — o texto
          // vai ao lado, senão vira warning no console e não aparece na tela.
          <Flex justify="center" align="center" gap={12} style={{ padding: 32 }}>
            <Spin />
            <Text type="secondary">Contando quantas venceram…</Text>
          </Flex>
        ) : (
          <>
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={
                <span>
                  <strong>{(modalVencidas as any).previa.total}</strong> oportunidade(s) com o prazo de proposta
                  vencido há mais de 1 dia, ainda sem proposta nossa.
                </span>
              }
              description={
                <Text style={{ fontSize: 12 }}>
                  O prazo usado é o de cada fonte (PNCP: encerramento da proposta; ConLicitação: prazo do boletim),
                  nunca a data de abertura sozinha. Arquivar é reversível e não apaga nada.
                </Text>
              } />

            <Flex gap={24} wrap="wrap" style={{ marginBottom: 12 }}>
              <Statistic title="Total" value={(modalVencidas as any).previa.total} />
              {Object.entries((modalVencidas as any).previa.por_fonte || {}).map(([f, n]) => (
                <Statistic key={f} title={f} value={n as number} valueStyle={{ fontSize: 20 }} />
              ))}
              <Statistic title="Valor estimado somado"
                value={`R$ ${Number((modalVencidas as any).previa.valor_total || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                valueStyle={{ fontSize: 20 }} />
            </Flex>

            <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Prazo mais antigo">
                {fmtDataBr((modalVencidas as any).previa.prazo_mais_antigo)}
              </Descriptions.Item>
              <Descriptions.Item label="Prazo mais recente">
                {fmtDataBr((modalVencidas as any).previa.prazo_mais_recente)}
              </Descriptions.Item>
              <Descriptions.Item label="De onde veio o prazo" span={2}>
                {Object.entries((modalVencidas as any).previa.por_origem_prazo || {})
                  .map(([k, n]) => `${k}: ${n}`).join(' · ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Ficam de fora (não são tocadas)" span={2}>
                <Text style={{ fontSize: 12 }}>
                  {(() => {
                    const f = (modalVencidas as any).previa.ficam_de_fora || {};
                    return `${f.sem_prazo ?? 0} sem prazo informado · ${f.com_proposta ?? 0} com proposta vinculada · `
                      + `${f.pode_voltar ?? 0} prorrogadas/suspensas · ${f.com_responsavel ?? 0} com responsável`;
                  })()}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Text type="secondary" style={{ fontSize: 11 }}>Amostra (as 10 de maior valor):</Text>
            <Table
              size="small" rowKey="id" pagination={false}
              dataSource={(modalVencidas as any).previa.amostra || []}
              scroll={{ x: 640 }} tableLayout="fixed"
              style={{ marginTop: 6 }}
              columns={[
                { title: 'UF', dataIndex: 'uf', width: 50 },
                { title: 'Órgão', dataIndex: 'orgao', width: 180, ellipsis: true },
                { title: 'Objeto', dataIndex: 'objeto', ellipsis: true },
                { title: 'Prazo', dataIndex: 'prazo', width: 100,
                  render: (v: string) => fmtDataBr(v) },
                { title: 'Valor', dataIndex: 'valor_estimado', width: 120, align: 'right' as const,
                  render: (v: any) => v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
}
